// MARK://dhk8/pos/pages/floor/ReservationsDrawer
// Reservations panel docked on the left side of FloorPage.
//
// Lane #163 redesign:
//   - Tabs HOY / MAÑANA / 7 DÍAS / RANGO (custom date picker)
//   - Form crear con date picker + email + sillas/accesibilidad + alergias
//     separadas + ocasión + origen + tableId opcional
//   - Pill destacada con alergias / sillas en card si presente
//   - El mesero puede:
//       - tap "+" para crear reserva
//       - tap "Llegó" → marca ARRIVED
//       - tap "Sentar" (después de Llegó) → abre PosTableSession + /order
//       - tap "No-show" / "Cancelar"

import { useEffect, useMemo, useState } from 'react'
import {
  Plus, Phone, X, RefreshCw, Clock, Users, Check, Calendar,
  Mail, Baby, Accessibility, AlertTriangle, Gift, MapPin,
} from 'lucide-react'
import {
  listReservations,
  createReservation,
  updateReservation,
  cancelReservation,
  type ReservationDto,
  type CreateReservationInput,
} from '../../lib/clients/floor.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { businessDayOf } from '../../lib/business-day.js'

const POLL_MS = 30_000

type DateMode = 'today' | 'tomorrow' | 'week' | 'range'

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function shiftISO(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}
function fmtDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })
}
function statusLabel(s: ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'Confirmada'
    case 'ARRIVED': return 'Llegó'
    case 'SEATED': return 'Sentada'
    case 'CANCELLED': return 'Cancelada'
    case 'NO_SHOW': return 'No-show'
  }
}
function statusTone(s: ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'tone-info'
    case 'ARRIVED': return 'tone-warn'
    case 'SEATED': return 'tone-ok'
    case 'CANCELLED': return 'tone-mute'
    case 'NO_SHOW': return 'tone-mute'
  }
}

const OCCASION_LABEL: Record<NonNullable<ReservationDto['occasion']>, string> = {
  birthday: 'Cumpleaños',
  anniversary: 'Aniversario',
  date: 'Cita',
  business: 'Negocios',
  other: 'Otro',
}

const SOURCE_LABEL: Record<NonNullable<ReservationDto['source']>, string> = {
  walk_in: 'Walk-in',
  phone: 'Llamada',
  web: 'Web',
  instagram: 'Instagram',
  recommended: 'Recomendado',
  other: 'Otro',
}

export interface ReservationsDrawerProps {
  /** Called when the user picks "Llegó" or "Sentar" — parent maps reservation
   *  to opening the table/session (FloorPage already knows how). */
  onSeatReservation?: (r: ReservationDto) => Promise<void> | void
}

export default function ReservationsDrawer({ onSeatReservation }: ReservationsDrawerProps) {
  const confirm = useConfirm()
  const [items, setItems] = useState<ReservationDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [mode, setMode] = useState<DateMode>('today')
  const [rangeFrom, setRangeFrom] = useState<string>(todayISO())
  const [rangeTo, setRangeTo] = useState<string>(shiftISO(7))

  // Compute the query window based on the active tab.
  const queryWindow = useMemo<{ date?: string; from?: string; to?: string; label: string }>(() => {
    if (mode === 'today') return { date: todayISO(), label: 'Hoy' }
    if (mode === 'tomorrow') return { date: shiftISO(1), label: 'Mañana' }
    if (mode === 'week') {
      return {
        from: new Date(`${todayISO()}T00:00:00`).toISOString(),
        to: new Date(`${shiftISO(7)}T23:59:59`).toISOString(),
        label: '7 días',
      }
    }
    // range
    return {
      from: new Date(`${rangeFrom}T00:00:00`).toISOString(),
      to: new Date(`${rangeTo}T23:59:59`).toISOString(),
      label: `${rangeFrom} → ${rangeTo}`,
    }
  }, [mode, rangeFrom, rangeTo])

  async function reload() {
    try {
      setError(null)
      setLoading(true)
      const opts: { date?: string; from?: string; to?: string } = {}
      if (queryWindow.date) opts.date = queryWindow.date
      if (queryWindow.from) opts.from = queryWindow.from
      if (queryWindow.to) opts.to = queryWindow.to
      const r = await listReservations(opts)
      // Orden cronológico por reservedAt
      r.sort((a, b) => a.reservedAt.localeCompare(b.reservedAt))
      setItems(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar reservas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    const iv = setInterval(() => void reload(), POLL_MS)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rangeFrom, rangeTo])

  // Agrupar por día cuando el rango cubre > 1 día (week/range).
  const grouped = useMemo(() => {
    if (mode === 'today' || mode === 'tomorrow') {
      const upcoming = items.filter((r) => r.status === 'CONFIRMED' || r.status === 'ARRIVED')
      const seated = items.filter((r) => r.status === 'SEATED')
      const inactive = items.filter((r) => r.status === 'CANCELLED' || r.status === 'NO_SHOW')
      return [{ key: 'upcoming', label: 'Próximas', rows: upcoming, muted: false },
              { key: 'seated',   label: 'Sentadas', rows: seated,   muted: true },
              { key: 'inactive', label: 'Inactivas', rows: inactive, muted: true }]
        .filter((g) => g.rows.length > 0)
    }
    // Para rango: agrupar por fecha (YYYY-MM-DD)
    const byDate = new Map<string, ReservationDto[]>()
    for (const r of items) {
      if (r.status === 'CANCELLED' || r.status === 'NO_SHOW') continue
      // Día del NEGOCIO, no UTC (mismo bug que el calendario). Ver lib/business-day.ts.
      const key = businessDayOf(r)
      if (!byDate.has(key)) byDate.set(key, [])
      byDate.get(key)!.push(r)
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, rows]) => ({
        key: date, label: fmtDate(`${date}T12:00:00`), rows, muted: false,
      }))
  }, [items, mode])

  async function handleMarkArrived(r: ReservationDto) {
    await updateReservation(r.id, { status: 'ARRIVED' })
    await reload()
    if (onSeatReservation) await onSeatReservation(r)
  }
  async function handleMarkNoShow(r: ReservationDto) {
    await updateReservation(r.id, { status: 'NO_SHOW' })
    await reload()
  }
  async function handleCancel(r: ReservationDto) {
    if (!(await confirm({ title: 'Cancelar reserva', message: `¿Cancelar reserva de ${r.name}?`, confirmLabel: 'Cancelar reserva', cancelLabel: 'No', tone: 'danger' }))) return
    await cancelReservation(r.id)
    await reload()
  }

  return (
    <aside className="dhk8-reservations">
      <header className="dhk8-reservations__head">
        <h2 className="dhk8-reservations__title">
          <Calendar size={14} /> Reservas
        </h2>
        <div className="dhk8-reservations__head-actions">
          <button type="button" className="dhk8-reservations__icon-btn" title="Recargar" onClick={() => void reload()}>
            <RefreshCw size={14} />
          </button>
          <button type="button" className="dhk8-reservations__cta" onClick={() => setCreating(true)}>
            <Plus size={14} /> Nueva
          </button>
        </div>
      </header>

      <div className="dhk8-reservations__tabs">
        <button type="button" className={`dhk8-reservations__tab${mode === 'today' ? ' is-active' : ''}`}    onClick={() => setMode('today')}>Hoy</button>
        <button type="button" className={`dhk8-reservations__tab${mode === 'tomorrow' ? ' is-active' : ''}`} onClick={() => setMode('tomorrow')}>Mañana</button>
        <button type="button" className={`dhk8-reservations__tab${mode === 'week' ? ' is-active' : ''}`}     onClick={() => setMode('week')}>7 días</button>
        <button type="button" className={`dhk8-reservations__tab${mode === 'range' ? ' is-active' : ''}`}    onClick={() => setMode('range')} title="Rango personalizado">
          <Clock size={11} />
        </button>
      </div>

      {mode === 'range' && (
        <div className="dhk8-reservations__range">
          <label>
            <span>De</span>
            <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} />
          </label>
          <label>
            <span>A</span>
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} />
          </label>
        </div>
      )}

      {error && <div className="dhk8-reservations__err">{error}</div>}
      {loading && <div className="dhk8-reservations__hint">Cargando…</div>}
      {!loading && grouped.length === 0 && (
        <div className="dhk8-reservations__empty">Sin reservas en {queryWindow.label.toLowerCase()}.</div>
      )}

      {grouped.map((g) => (
        <div key={g.key}>
          <div className="dhk8-reservations__section">{g.label}</div>
          <ul className="dhk8-reservations__list">
            {g.rows.map((r) => (
              <ReservationCard
                key={r.id}
                r={r}
                muted={g.muted}
                onMarkArrived={handleMarkArrived}
                onSeat={onSeatReservation}
                onMarkNoShow={handleMarkNoShow}
                onCancel={handleCancel}
                showDate={mode === 'week' || mode === 'range'}
              />
            ))}
          </ul>
        </div>
      ))}

      {creating && (
        <CreateReservationForm
          defaultDate={mode === 'tomorrow' ? shiftISO(1) : (mode === 'today' ? todayISO() : todayISO())}
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await reload()
          }}
        />
      )}

      <style>{drawerStyles}</style>
    </aside>
  )
}

// ── Card ────────────────────────────────────────────────────────────────────

function ReservationCard({ r, muted, showDate, onMarkArrived, onSeat, onMarkNoShow, onCancel }: {
  r: ReservationDto
  muted: boolean
  showDate: boolean
  onMarkArrived: (r: ReservationDto) => Promise<void> | void
  onSeat?: (r: ReservationDto) => Promise<void> | void
  onMarkNoShow: (r: ReservationDto) => Promise<void> | void
  onCancel: (r: ReservationDto) => Promise<void> | void
}) {
  const isActive = r.status === 'CONFIRMED' || r.status === 'ARRIVED'
  return (
    <li className={`dhk8-reservations__card${muted ? ' dhk8-reservations__card--muted' : ''}`}>
      <div className="dhk8-reservations__card-row">
        <span className="dhk8-reservations__time">
          {showDate && <span className="dhk8-reservations__date">{fmtDate(r.reservedAt)} · </span>}
          {fmtTime(r.reservedAt)}
        </span>
        <span className={`dhk8-reservations__badge ${statusTone(r.status)}`}>{statusLabel(r.status)}</span>
      </div>
      <div className="dhk8-reservations__card-row dhk8-reservations__card-name">
        <strong>{r.name}</strong>
        <span className="dhk8-reservations__party"><Users size={11} /> {r.partySize}</span>
      </div>

      {/* Pills: ocasión, alergias destacadas, accesibilidad, source */}
      <div className="dhk8-reservations__pills">
        {r.reservationCode && <span className="dhk8-reservations__pill dhk8-reservations__pill--code">{r.reservationCode}</span>}
        {r.occasion && <span className="dhk8-reservations__pill"><Gift size={9} /> {OCCASION_LABEL[r.occasion]}</span>}
        {r.highChair && <span className="dhk8-reservations__pill"><Baby size={9} /> Silla bebé</span>}
        {r.wheelchair && <span className="dhk8-reservations__pill"><Accessibility size={9} /> Accesible</span>}
        {r.source && <span className="dhk8-reservations__pill dhk8-reservations__pill--src">{SOURCE_LABEL[r.source]}</span>}
      </div>

      {r.allergies && (
        <div className="dhk8-reservations__allergies">
          <AlertTriangle size={11} /> {r.allergies}
        </div>
      )}

      {(r.phone || r.email) && (
        <div className="dhk8-reservations__contact">
          {r.phone && (<><Phone size={10} /> <a href={`tel:${r.phone}`}>{r.phone}</a></>)}
          {r.phone && r.email && <span className="dhk8-reservations__sep">·</span>}
          {r.email && (<><Mail size={10} /> <a href={`mailto:${r.email}`}>{r.email}</a></>)}
        </div>
      )}

      {r.tableId && (
        <div className="dhk8-reservations__assigned">
          <MapPin size={10} /> Mesa asignada
        </div>
      )}

      {r.notes && <div className="dhk8-reservations__notes">{r.notes}</div>}

      {isActive && (
        <div className="dhk8-reservations__card-actions">
          {r.status === 'CONFIRMED' && (
            <button type="button" className="dhk8-reservations__btn primary" onClick={() => void onMarkArrived(r)}>
              <Check size={12} /> Llegó
            </button>
          )}
          {r.status === 'ARRIVED' && (
            <button type="button" className="dhk8-reservations__btn primary" onClick={() => onSeat && void onSeat(r)}>
              Sentar
            </button>
          )}
          <button type="button" className="dhk8-reservations__btn" onClick={() => void onMarkNoShow(r)} title="Marcar no-show">
            No-show
          </button>
          <button type="button" className="dhk8-reservations__btn danger" onClick={() => void onCancel(r)} title="Cancelar reserva">
            <X size={12} />
          </button>
        </div>
      )}
    </li>
  )
}

// ── Create form ─────────────────────────────────────────────────────────────

function CreateReservationForm({
  defaultDate,
  onCancel,
  onCreated,
}: {
  defaultDate: string
  onCancel: () => void
  onCreated: () => Promise<void> | void
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [partySize, setPartySize] = useState(2)
  const [date, setDate] = useState(defaultDate)
  const [time, setTime] = useState(() => {
    const d = new Date()
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  })
  const [durationMin, setDurationMin] = useState(90)
  const [highChair, setHighChair] = useState(false)
  const [wheelchair, setWheelchair] = useState(false)
  const [occasion, setOccasion] = useState<'' | NonNullable<ReservationDto['occasion']>>('')
  const [source, setSource] = useState<'' | NonNullable<ReservationDto['source']>>('walk_in')
  const [allergies, setAllergies] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return setErr('Nombre requerido')
    if (!date) return setErr('Fecha requerida')
    setBusy(true)
    setErr(null)
    try {
      // Construir Date con fecha+hora local (sin tocar timezone — el browser
      // ya envía local-time; el backend deserializa con TZ del cluster).
      const [hh, mm] = time.split(':').map(Number)
      const [yy, mo, dd] = date.split('-').map(Number)
      const d = new Date(yy ?? 2026, (mo ?? 1) - 1, dd ?? 1, hh ?? 19, mm ?? 0, 0, 0)
      if (Number.isNaN(d.getTime())) return setErr('Fecha/hora inválida')
      const input: CreateReservationInput = {
        name: name.trim(),
        partySize,
        reservedAt: d.toISOString(),
        durationMin,
        highChair,
        wheelchair,
      }
      if (phone.trim()) input.phone = phone.trim()
      if (email.trim()) input.email = email.trim()
      if (notes.trim()) input.notes = notes.trim()
      if (allergies.trim()) input.allergies = allergies.trim()
      if (occasion) input.occasion = occasion
      if (source) input.source = source
      await createReservation(input)
      await onCreated()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al crear reserva')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dhk8-reservations__modal-bg" onClick={onCancel}>
      <form
        className="dhk8-reservations__modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
      >
        <h3>Nueva reserva</h3>

        <label>
          <span>Nombre</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
        </label>

        <div className="dhk8-reservations__modal-row">
          <label>
            <span>Teléfono</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+52..." />
          </label>
          <label>
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@..." />
          </label>
        </div>

        <div className="dhk8-reservations__modal-row">
          <label>
            <span>Personas</span>
            <input
              type="number"
              min={1}
              max={50}
              value={partySize}
              onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))}
            />
          </label>
          <label>
            <span>Duración (min)</span>
            <input
              type="number"
              min={15}
              max={600}
              step={15}
              value={durationMin}
              onChange={(e) => setDurationMin(Math.max(15, Number(e.target.value) || 90))}
            />
          </label>
        </div>

        <div className="dhk8-reservations__modal-row">
          <label>
            <span>Fecha</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={todayISO()} required />
          </label>
          <label>
            <span>Hora</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
          </label>
        </div>

        <div className="dhk8-reservations__modal-row">
          <label>
            <span>Ocasión</span>
            <select value={occasion} onChange={(e) => setOccasion(e.target.value as typeof occasion)}>
              <option value="">—</option>
              <option value="birthday">Cumpleaños</option>
              <option value="anniversary">Aniversario</option>
              <option value="date">Cita</option>
              <option value="business">Negocios</option>
              <option value="other">Otro</option>
            </select>
          </label>
          <label>
            <span>Origen</span>
            <select value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
              <option value="walk_in">Walk-in</option>
              <option value="phone">Llamada</option>
              <option value="web">Web</option>
              <option value="instagram">Instagram</option>
              <option value="recommended">Recomendado</option>
              <option value="other">Otro</option>
            </select>
          </label>
        </div>

        <div className="dhk8-reservations__checks">
          <label className="dhk8-reservations__check">
            <input type="checkbox" checked={highChair} onChange={(e) => setHighChair(e.target.checked)} />
            <Baby size={12} /> Silla para bebé
          </label>
          <label className="dhk8-reservations__check">
            <input type="checkbox" checked={wheelchair} onChange={(e) => setWheelchair(e.target.checked)} />
            <Accessibility size={12} /> Accesibilidad
          </label>
        </div>

        <label>
          <span>Alergias / restricciones</span>
          <input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Maní, gluten, vegetariano..." />
        </label>

        <label>
          <span>Notas (opcional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mesa cerca ventana, sorpresa..." />
        </label>

        {err && <div className="dhk8-reservations__err">{err}</div>}

        <div className="dhk8-reservations__modal-actions">
          <button type="button" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button type="submit" className="primary" disabled={busy}>{busy ? 'Guardando…' : 'Crear reserva'}</button>
        </div>
      </form>
    </div>
  )
}

const drawerStyles = `
  .dhk8-reservations {
    display: flex; flex-direction: column; gap: 6px;
    width: 300px; min-width: 260px; max-width: 340px;
    padding: 10px; overflow-y: auto;
    background: var(--bg-surface, #0f0f0f); border: 1px solid var(--border, #2a2a2a);
    border-radius: var(--radius, 4px); color: var(--ink, #f0f0f0);
    font-family: var(--font-mono, monospace); font-size: 12px;
  }
  .dhk8-reservations__head { display: flex; align-items: center; justify-content: space-between; gap: 6px; padding-bottom: 6px; border-bottom: 1px solid var(--border, #2a2a2a); }
  .dhk8-reservations__title { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 13px; font-weight: 600; }
  .dhk8-reservations__head-actions { display: inline-flex; gap: 4px; }
  .dhk8-reservations__icon-btn { padding: 4px; background: transparent; border: 1px solid var(--border, #2a2a2a); color: inherit; border-radius: 4px; cursor: pointer; }
  .dhk8-reservations__icon-btn:hover { background: var(--bg-elevated, #1a1a1a); }
  .dhk8-reservations__cta { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; background: var(--tint, #4caf50); color: var(--bg-canvas, #000); border: none; border-radius: 4px; font-family: inherit; font-size: 11px; font-weight: 600; cursor: pointer; }

  .dhk8-reservations__tabs { display: flex; gap: 2px; padding: 4px 0; }
  .dhk8-reservations__tab { flex: 1; padding: 5px 4px; background: transparent; border: 1px solid var(--border, #2a2a2a); color: var(--text-secondary, #bbb); font-family: inherit; font-size: 10px; cursor: pointer; border-radius: 4px; transition: background 120ms ease; display: inline-flex; align-items: center; justify-content: center; gap: 3px; }
  .dhk8-reservations__tab:hover { background: var(--bg-elevated, #1a1a1a); }
  .dhk8-reservations__tab.is-active { background: var(--tint, #4caf50); color: var(--bg-canvas, #000); border-color: var(--tint, #4caf50); font-weight: 600; }

  .dhk8-reservations__range { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 4px 0; }
  .dhk8-reservations__range label { display: flex; flex-direction: column; gap: 2px; font-size: 10px; color: var(--text-tertiary, #999); }
  .dhk8-reservations__range input { padding: 4px 6px; background: var(--bg-canvas, #000); border: 1px solid var(--border, #2a2a2a); color: inherit; border-radius: 4px; font-family: inherit; font-size: 11px; }

  .dhk8-reservations__section { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-tertiary, #999); padding: 6px 0 2px; }
  .dhk8-reservations__list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
  .dhk8-reservations__card { padding: 8px; background: var(--bg-canvas, #000); border: 1px solid var(--border, #2a2a2a); border-radius: 4px; display: flex; flex-direction: column; gap: 4px; }
  .dhk8-reservations__card--muted { opacity: 0.6; }
  .dhk8-reservations__card-row { display: flex; justify-content: space-between; align-items: center; gap: 4px; }
  .dhk8-reservations__time { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
  .dhk8-reservations__date { font-size: 10px; font-weight: 400; color: var(--text-tertiary, #999); margin-right: 4px; }
  .dhk8-reservations__badge { padding: 2px 6px; font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; border-radius: 999px; border: 1px solid currentColor; }
  .dhk8-reservations__badge.tone-info { color: var(--status-info, #4d9eff); }
  .dhk8-reservations__badge.tone-warn { color: var(--status-warning, #e0a23c); }
  .dhk8-reservations__badge.tone-ok { color: var(--tint, #4caf50); }
  .dhk8-reservations__badge.tone-mute { color: var(--text-tertiary, #999); }
  .dhk8-reservations__card-name { font-size: 13px; }
  .dhk8-reservations__party { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: var(--text-secondary, #bbb); }

  .dhk8-reservations__pills { display: flex; flex-wrap: wrap; gap: 3px; }
  .dhk8-reservations__pill { display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px; font-size: 9px; letter-spacing: 0.04em; background: var(--bg-elevated, #1a1a1a); border: 1px solid var(--border, #2a2a2a); border-radius: 999px; color: var(--text-secondary, #bbb); }
  .dhk8-reservations__pill--code { background: var(--tint-bg, rgba(76,175,80,0.15)); color: var(--tint, #4caf50); border-color: var(--tint, #4caf50); font-weight: 600; font-family: var(--font-mono, monospace); }
  .dhk8-reservations__pill--src { color: var(--text-tertiary, #999); }

  .dhk8-reservations__allergies { display: inline-flex; align-items: center; gap: 4px; padding: 4px 6px; background: rgba(224, 162, 60, 0.12); color: var(--status-warning, #e0a23c); border: 1px dashed var(--status-warning, #e0a23c); border-radius: 4px; font-size: 11px; font-weight: 500; }
  .dhk8-reservations__contact { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px; font-size: 11px; color: var(--text-secondary, #bbb); }
  .dhk8-reservations__contact a { color: inherit; text-decoration: none; }
  .dhk8-reservations__contact a:hover { text-decoration: underline; }
  .dhk8-reservations__sep { color: var(--text-tertiary, #999); }
  .dhk8-reservations__assigned { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; color: var(--tint, #4caf50); }
  .dhk8-reservations__notes { padding: 4px 6px; background: var(--bg-elevated, #1a1a1a); border-radius: 4px; font-size: 11px; color: var(--text-secondary, #bbb); }

  .dhk8-reservations__card-actions { display: flex; gap: 4px; padding-top: 4px; }
  .dhk8-reservations__btn { flex: 1; padding: 6px 4px; background: transparent; border: 1px solid var(--border, #2a2a2a); color: inherit; border-radius: 4px; font-family: inherit; font-size: 11px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 3px; transition: background 120ms ease; }
  .dhk8-reservations__btn:hover { background: var(--bg-elevated, #1a1a1a); }
  .dhk8-reservations__btn.primary { background: var(--tint, #4caf50); color: var(--bg-canvas, #000); border-color: var(--tint, #4caf50); font-weight: 600; }
  .dhk8-reservations__btn.danger { flex: 0 0 32px; color: var(--status-error, #e57373); }
  .dhk8-reservations__btn.danger:hover { background: rgba(229, 115, 115, 0.15); }

  .dhk8-reservations__err { padding: 6px 8px; background: rgba(229, 115, 115, 0.15); color: var(--status-error, #e57373); border: 1px solid var(--status-error, #e57373); border-radius: 4px; font-size: 11px; }
  .dhk8-reservations__hint, .dhk8-reservations__empty { padding: 12px; text-align: center; color: var(--text-tertiary, #999); font-size: 11px; }

  .dhk8-reservations__modal-bg { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 12px; }
  .dhk8-reservations__modal { background: var(--bg-elevated, #1a1a1a); border: 1px solid var(--border, #2a2a2a); padding: 16px; display: flex; flex-direction: column; gap: 10px; min-width: 360px; max-width: 480px; max-height: 92vh; overflow-y: auto; border-radius: 6px; box-shadow: 0 20px 40px rgba(0,0,0,0.5); }
  .dhk8-reservations__modal h3 { margin: 0; font-size: 15px; font-family: var(--font-display, sans-serif); }
  .dhk8-reservations__modal label { display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: var(--text-tertiary, #999); }
  .dhk8-reservations__modal input,
  .dhk8-reservations__modal select { padding: 8px; background: var(--bg-canvas, #000); border: 1px solid var(--border, #2a2a2a); color: inherit; border-radius: 4px; font-family: inherit; font-size: 13px; }
  .dhk8-reservations__modal-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .dhk8-reservations__checks { display: flex; flex-wrap: wrap; gap: 12px; padding: 4px 0; }
  .dhk8-reservations__check { display: inline-flex; flex-direction: row !important; align-items: center; gap: 6px; font-size: 12px !important; color: var(--ink, #f0f0f0) !important; cursor: pointer; }
  .dhk8-reservations__check input { width: 16px; height: 16px; }
  .dhk8-reservations__modal-actions { display: flex; justify-content: flex-end; gap: 8px; padding-top: 8px; }
  .dhk8-reservations__modal-actions button { min-height: 36px; padding: 6px 14px; background: transparent; border: 1px solid var(--border, #2a2a2a); color: inherit; border-radius: 4px; font-family: inherit; font-size: 12px; cursor: pointer; }
  .dhk8-reservations__modal-actions button.primary { background: var(--tint, #4caf50); color: var(--bg-canvas, #000); border-color: var(--tint, #4caf50); font-weight: 600; }
  .dhk8-reservations__modal-actions button:disabled { opacity: 0.5; cursor: not-allowed; }
`
